import { PermissionGuard } from '@/components/PermissionGuard';
import { Switch } from '@/components/ui/switch';
import { fadeUp, stagger } from '@/lib/animations';
import { formatDate, timeSinceActivation } from '@/lib/format';
import { lineMapping } from '@/lib/lineMapping';
import {
  optionDescriptionMapping,
  optionMapping,
} from '@/lib/optionMapping';
import type { MobileLine } from '@octave/contracts';
import { motion } from 'framer-motion';
import {
  Building2,
  Calendar,
  Clock,
  Hash,
  Loader2,
  Signal,
} from 'lucide-react';
import { useState } from 'react';
import { ConfirmTogglePackageOptionDialog } from './ConfirmTogglePackageOptionDialog';
import { DetailRow } from './DetailRow';
import { isLineActionsDisabled } from './helpers';

interface LineInfoTabProps {
  lineId: string;
  lineDetails: MobileLine;
}

/** Line details card + package options card, with the option toggle dialog. */
export function LineInfoTab({ lineId, lineDetails }: LineInfoTabProps) {
  const [confirmToggleDialog, setConfirmToggleDialog] = useState<{
    isOpen: boolean;
    optionLabel: string;
    isEnabled: boolean;
    optionId: string;
    packageOptionLabel: string;
  } | null>(null);

  const type = lineDetails.phonePlan.isDataOnly ? 'dataonly' : 'classic';
  const typeLabel = type ? lineMapping[type] || type : '-';

  return (
    <>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 gap-4 lg:grid-cols-2"
      >
        {/* Line details card */}
        <motion.div
          variants={fadeUp}
          className="rounded-2xl bg-white p-6 shadow-sm"
        >
          <h2 className="text-lg font-bold text-slate-900">
            Détails de la ligne
          </h2>
          <p className="mt-0.5 text-sm text-slate-400">
            Informations générales
          </p>
          <div className="mt-6 space-y-4">
            <DetailRow icon={Signal} label="Type" value={typeLabel} />
            <DetailRow
              icon={Calendar}
              label="Activation"
              value={formatDate(lineDetails.activationDate ?? undefined)}
            />
            <DetailRow
              icon={Clock}
              label="Ancienneté"
              value={timeSinceActivation(lineDetails.activationDate ?? undefined)}
            />
            <DetailRow
              icon={Building2}
              label="Centre de facturation"
              value={lineDetails.site.name}
            />
            {lineDetails.portingCode && (
              <DetailRow
                icon={Hash}
                label="Code RIO"
                value={lineDetails.portingCode}
                mono
              />
            )}
          </div>
        </motion.div>

        {/* Package options card */}
        <motion.div
          variants={fadeUp}
          className="rounded-2xl bg-white p-6 shadow-sm"
        >
          <h2 className="text-lg font-bold text-slate-900">
            Options du forfait
          </h2>
          <p className="mt-0.5 text-sm text-slate-400">
            Gérez les options de votre ligne
          </p>
          <div className="mt-6 space-y-3">
            {lineDetails.packageOptions
              ?.filter(
                (option) =>
                  Object.keys(optionMapping).includes(option.label) &&
                  (option.enabled || option.inProgress)
              )
              .map((option) => {
                const isPending =
                  confirmToggleDialog?.optionId === option.label;
                const isInProgress = option.inProgress;
                return (
                  <div
                    key={option.label}
                    className={`flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 transition-colors ${
                      isInProgress ? 'opacity-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        {optionMapping[option.label] || option.label}
                      </p>
                      {optionDescriptionMapping[option.label] && (
                        <p className="mt-0.5 text-xs text-slate-400 line-clamp-1">
                          {optionDescriptionMapping[option.label]}
                        </p>
                      )}
                      {isInProgress && (
                        <span className="mt-1 inline-flex items-center gap-1 text-xs text-sky-600">
                          <Loader2 className="h-3 w-3 animate-spin" />{' '}
                          Changement en cours
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <PermissionGuard permission="write">
                        <Switch
                          checked={
                            isPending
                              ? confirmToggleDialog.isEnabled
                              : option.active
                          }
                          onCheckedChange={(checked) => {
                            setConfirmToggleDialog({
                              isOpen: true,
                              optionLabel:
                                optionMapping[option.label] || option.label,
                              packageOptionLabel: option.label,
                              isEnabled: checked,
                              optionId: option.label,
                            });
                          }}
                          disabled={
                            isInProgress ||
                            !option.enabled ||
                            isPending ||
                            isLineActionsDisabled(lineDetails.status)
                          }
                        />
                      </PermissionGuard>
                      <PermissionGuard permission="read">
                        <span
                          className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                            option.active
                              ? 'bg-emerald-50 text-emerald-600'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {option.active ? 'Activé' : 'Désactivé'}
                        </span>
                      </PermissionGuard>
                    </div>
                  </div>
                );
              })}
          </div>
        </motion.div>
      </motion.div>

      {confirmToggleDialog && (
        <ConfirmTogglePackageOptionDialog
          isOpen={confirmToggleDialog.isOpen}
          onClose={() => setConfirmToggleDialog(null)}
          onConfirm={() => setConfirmToggleDialog(null)}
          optionLabel={confirmToggleDialog.optionLabel}
          packageOptionLabel={confirmToggleDialog.packageOptionLabel}
          isEnabled={confirmToggleDialog.isEnabled}
          lineId={lineId}
        />
      )}
    </>
  );
}
